import type {
  Allocation, CompositionLine, Flag, Material, MaterialAnswer, Portal, Taxonomy,
} from './types'

/**
 * The rules a submission must meet, in one place so the form, the review
 * screen and the submit button cannot disagree. The API enforces the same
 * ones (and the database the totals), so a form cannot save what a script
 * could not.
 *
 * Percentages carry one decimal place. Sums are taken in tenths, as
 * integers, so 33.3 + 33.3 + 33.4 is exactly 100 and not 99.99999.
 */

const tenths = (pct: number) => Math.round(pct * 10)

/** The sum of some percentages, to one decimal place. */
export function total(values: number[]): number {
  return values.reduce((sum, v) => sum + tenths(v), 0) / 10
}

export const compositionTotal = (lines: CompositionLine[]) => total(lines.map((l) => l.sharePct))
export const allocationTotal = (allocations: Allocation[]) => total(allocations.map((a) => a.volumeSharePct))

/** Exactly 100, compared in tenths. */
export const isComplete = (pct: number) => tenths(pct) === 1000

/** A share a person may type: above 0, at most 100, one decimal place. */
export function validShare(pct: number): boolean {
  return Number.isFinite(pct) && pct > 0 && pct <= 100 && Math.abs(pct * 10 - Math.round(pct * 10)) < 1e-9
}

/** The ingredients a material may use: its commodity's for a fixed one, any for a blend. */
export function allowedIngredients(material: Material, taxonomy: Taxonomy) {
  return material.kind === 'fixed'
    ? taxonomy.ingredients.filter((f) => f.commodityId === material.commodityId)
    : taxonomy.ingredients
}

export type CompositionIssue =
  | { kind: 'remaining'; pct: number }
  | { kind: 'over'; pct: number }
  | { kind: 'empty' }
  | { kind: 'foreign'; ingredientId: string }
  | { kind: 'duplicate'; ingredientId: string }
  | { kind: 'invalid_share'; ingredientId: string }

/** What stops a composition from being saved as complete. Empty when it is. */
export function compositionIssues(material: Material, lines: CompositionLine[], taxonomy: Taxonomy): CompositionIssue[] {
  const issues: CompositionIssue[] = []
  if (!lines.length) return [{ kind: 'empty' }]
  const allowed = new Set(allowedIngredients(material, taxonomy).map((f) => f.id))
  const seen = new Set<string>()
  for (const line of lines) {
    if (!allowed.has(line.ingredientId)) issues.push({ kind: 'foreign', ingredientId: line.ingredientId })
    if (seen.has(line.ingredientId)) issues.push({ kind: 'duplicate', ingredientId: line.ingredientId })
    if (!validShare(line.sharePct)) issues.push({ kind: 'invalid_share', ingredientId: line.ingredientId })
    seen.add(line.ingredientId)
  }
  const sum = compositionTotal(lines)
  if (sum < 100) issues.push({ kind: 'remaining', pct: total([100, -sum]) })
  if (sum > 100) issues.push({ kind: 'over', pct: total([sum, -100]) })
  return issues
}

/** Per commodity, for a blend's subtotals. */
export function subtotalsByCommodity(lines: CompositionLine[], taxonomy: Taxonomy): Map<string, number> {
  const commodityOf = new Map(taxonomy.ingredients.map((f) => [f.id, f.commodityId]))
  const out = new Map<string, number>()
  for (const line of lines) {
    const c = commodityOf.get(line.ingredientId)
    if (!c) continue
    out.set(c, total([out.get(c) ?? 0, line.sharePct]))
  }
  return out
}

export type AllocationIssue =
  | { kind: 'none' }
  | { kind: 'remaining'; pct: number }
  | { kind: 'over'; pct: number }
  | { kind: 'duplicate'; originId: string }
  | { kind: 'invalid_share'; originId: string }

/** What stops one ingredient's origins from covering its year's volume. */
export function allocationIssues(allocations: Allocation[] | undefined): AllocationIssue[] {
  if (!allocations?.length) return [{ kind: 'none' }]
  const issues: AllocationIssue[] = []
  const seen = new Set<string>()
  for (const a of allocations) {
    if (seen.has(a.originId)) issues.push({ kind: 'duplicate', originId: a.originId })
    if (!validShare(a.volumeSharePct)) issues.push({ kind: 'invalid_share', originId: a.originId })
    seen.add(a.originId)
  }
  const sum = allocationTotal(allocations)
  if (sum < 100) issues.push({ kind: 'remaining', pct: total([100, -sum]) })
  if (sum > 100) issues.push({ kind: 'over', pct: total([sum, -100]) })
  return issues
}

/** Tonnes from one origin: the material's volume × the ingredient's share × the origin's share. */
export function originTonnes(material: Material, ingredientSharePct: number, volumeSharePct: number): number {
  return (material.annualVolumeT * ingredientSharePct * volumeSharePct) / 10000
}

export type MaterialState = 'not_started' | 'in_progress' | 'complete' | 'needs_attention'

export interface MaterialProgress {
  state: MaterialState
  compositionDone: boolean
  /** Ingredients of the composition still without origins covering 100%. */
  ingredientsWithoutOrigins: string[]
  /** Pre-filled from last year and not yet confirmed. */
  awaitingConfirmation: boolean
  openFlags: Flag[]
  failedChecks: number
  /** Origins of a commodity that requires farm links, used here without any. */
  originsWithoutFarms: string[]
}

/** Where one material stands, for the home screen and the submit check. */
export function materialProgress(portal: Portal, material: Material): MaterialProgress {
  const answer: MaterialAnswer | undefined = portal.submission.answers[material.id]
  const composition = answer?.composition ?? []
  const compositionDone = Boolean(answer?.confirmed) && compositionIssues(material, composition, portal.taxonomy).length === 0
  const ingredientsWithoutOrigins = composition
    .filter((line) => allocationIssues(answer?.allocations[line.ingredientId]).length > 0)
    .map((line) => line.ingredientId)
  const openFlags = portal.submission.flags.filter((f) => f.target.materialId === material.id && !f.addressed)
  const usedOrigins = new Set(composition.flatMap((line) => (answer?.allocations[line.ingredientId] ?? []).map((a) => a.originId)))
  const failedChecks = portal.origins.filter((o) => usedOrigins.has(o.id) && o.check.state === 'failed').length
  // Where the commodity asks for it, an origin must name the farms behind it.
  const commodityOf = new Map(portal.taxonomy.ingredients.map((f) => [f.id, f.commodityId]))
  const required = new Set(portal.taxonomy.commodities.filter((c) => c.supplyRequired).map((c) => c.id))
  const originsWithoutFarms = [...new Set(composition
    .filter((line) => required.has(commodityOf.get(line.ingredientId) ?? ''))
    .flatMap((line) => (answer?.allocations[line.ingredientId] ?? []).map((a) => a.originId)))]
    .filter((id) => {
      const s = portal.origins.find((o) => o.id === id)?.supply
      return !s || s.links.length === 0
    })

  // The buyer's pre-fill is not the supplier's work yet.
  const buyerPrefill = Boolean(answer?.prefilledByBuyer && !answer.confirmed)
  const touched = !buyerPrefill && (composition.length > 0 || Object.values(answer?.allocations ?? {}).some((a) => a.length > 0))
  const state: MaterialState = openFlags.length
    ? 'needs_attention'
    : compositionDone && ingredientsWithoutOrigins.length === 0 && originsWithoutFarms.length === 0
      ? 'complete'
      : touched ? 'in_progress' : 'not_started'

  return {
    state,
    compositionDone,
    ingredientsWithoutOrigins: compositionDone ? ingredientsWithoutOrigins : [],
    awaitingConfirmation: Boolean((answer?.fromLastYear || answer?.prefilledByBuyer) && !answer?.confirmed),
    openFlags,
    failedChecks,
    originsWithoutFarms: compositionDone ? originsWithoutFarms : [],
  }
}

/**
 * One reason a material is not complete, structured so every screen says it
 * the same way and links to where it is fixed. The API answers the same list
 * for the submission (GET /supplier/submission `blockers`), and is the
 * authority; this is the instant version from the portal's own state.
 *
 * Per material, in this order: open reviewer flags; then the composition
 * (missing, short or over, or carried over from last year and not
 * confirmed); and only once that is done, each ingredient without origins (or
 * whose origins do not total 100%) and each origin of a commodity that
 * requires farms with none behind it. Failed checks never block: they are
 * `MaterialWarning`s. `composition_incomplete` and `origins_incomplete`
 * cannot come back from the API (the database keeps every saved total at
 * exactly 100); they cover answers the portal holds before saving.
 */
export type BlockerKind =
  | 'flag_open' | 'composition_missing' | 'composition_incomplete' | 'composition_unconfirmed'
  | 'ingredient_without_origins' | 'origins_incomplete' | 'origin_needs_farms'

export interface Blocker {
  kind: BlockerKind
  materialId: string
  ingredientId?: string
  originId?: string
  commodityId?: string
  flagId?: string
  /** For a composition or origins short of 100%, or over: how far. */
  pct?: number
  over?: boolean
}

export function materialBlockers(portal: Portal, material: Material): Blocker[] {
  const out: Blocker[] = []
  const answer = portal.submission.answers[material.id]
  const composition = answer?.composition ?? []
  for (const f of portal.submission.flags) {
    if (f.target.materialId !== material.id || f.addressed) continue
    out.push({
      kind: 'flag_open', materialId: material.id, flagId: f.id, ingredientId: f.target.ingredientId,
      ...(f.target.kind === 'origin' ? { originId: f.target.originId } : {}),
    })
  }
  if (!composition.length) return [...out, { kind: 'composition_missing', materialId: material.id }]
  const issues = compositionIssues(material, composition, portal.taxonomy)
  if (issues.length) {
    const short = issues.find((i) => i.kind === 'remaining')
    const over = issues.find((i) => i.kind === 'over')
    return [...out, {
      kind: 'composition_incomplete', materialId: material.id,
      ...(short && 'pct' in short ? { pct: short.pct } : over && 'pct' in over ? { pct: over.pct, over: true } : {}),
    }]
  }
  if (!answer?.confirmed) return [...out, { kind: 'composition_unconfirmed', materialId: material.id }]
  for (const line of composition) {
    const allocations = answer.allocations[line.ingredientId]
    if (!allocations?.length) { out.push({ kind: 'ingredient_without_origins', materialId: material.id, ingredientId: line.ingredientId }); continue }
    const a = allocationIssues(allocations)
    if (a.length) {
      const short = a.find((i) => i.kind === 'remaining')
      const over = a.find((i) => i.kind === 'over')
      out.push({
        kind: 'origins_incomplete', materialId: material.id, ingredientId: line.ingredientId,
        ...(short && 'pct' in short ? { pct: short.pct } : over && 'pct' in over ? { pct: over.pct, over: true } : {}),
      })
    }
  }
  const ingredient = new Map(portal.taxonomy.ingredients.map((f) => [f.id, f]))
  const required = new Set(portal.taxonomy.commodities.filter((c) => c.supplyRequired).map((c) => c.id))
  for (const line of composition) {
    const commodityId = ingredient.get(line.ingredientId)?.commodityId ?? ''
    if (!required.has(commodityId)) continue
    for (const a of answer.allocations[line.ingredientId] ?? []) {
      const s = portal.origins.find((o) => o.id === a.originId)?.supply
      if (!s || s.links.length === 0) out.push({ kind: 'origin_needs_farms', materialId: material.id, ingredientId: line.ingredientId, originId: a.originId, commodityId })
    }
  }
  return out
}

/** Every material's blockers, in the campaign's order: what stops the submission. */
export const submissionBlockers = (portal: Portal): Blocker[] => portal.materials.flatMap((m) => materialBlockers(portal, m))

/** Where a blocker is fixed: the material's composition or origins, at the ingredient or origin. */
export function blockerPath(b: Blocker): string {
  const step = b.kind === 'composition_missing' || b.kind === 'composition_incomplete' || b.kind === 'composition_unconfirmed'
    || (b.kind === 'flag_open' && !b.originId)
    ? 'composition' : 'origins'
  const anchor = b.originId ? `#origin-${b.originId}` : b.ingredientId && step === 'origins' ? `#ingredient-${b.ingredientId}` : ''
  return `/materials/${b.materialId}/${step}${anchor}`
}

/** Worth a look, but never stops a submission: origins whose check failed, origins whose farms include rejected plots. */
export interface MaterialWarning {
  kind: 'origin_check_failed' | 'plots_rejected'
  materialId: string
  originId: string
  count?: number
}

export function materialWarnings(portal: Portal, material: Material): MaterialWarning[] {
  const answer = portal.submission.answers[material.id]
  const used = new Set((answer?.composition ?? []).flatMap((l) => (answer?.allocations[l.ingredientId] ?? []).map((a) => a.originId)))
  const out: MaterialWarning[] = []
  for (const o of portal.origins) {
    if (!used.has(o.id)) continue
    if (o.check.state === 'failed') out.push({ kind: 'origin_check_failed', materialId: material.id, originId: o.id })
    else if ((o.supply?.rejected ?? 0) > 0) out.push({ kind: 'plots_rejected', materialId: material.id, originId: o.id, count: o.supply!.rejected })
  }
  return out
}

export interface Readiness {
  ready: boolean
  complete: number
  total: number
  /** Materials still to finish, in the campaign's order. */
  blocking: { material: Material; progress: MaterialProgress }[]
}

/** Submit is allowed only when every material is complete and every flag addressed. */
export function readiness(portal: Portal): Readiness {
  const all = portal.materials.map((material) => ({ material, progress: materialProgress(portal, material) }))
  const blocking = all.filter((m) => m.progress.state !== 'complete')
  return { ready: blocking.length === 0, complete: all.length - blocking.length, total: all.length, blocking }
}

/** Whether the supplier may change anything. Draft and returned only. */
export const editable = (portal: Portal) =>
  portal.submission.status === 'draft' || portal.submission.status === 'returned'

/**
 * In a returned submission, only what the reviewer flagged is open again; the
 * rest stays as submitted, so a resubmission changes what was asked about.
 */
export function materialEditable(portal: Portal, materialId: string): boolean {
  if (portal.submission.status === 'draft') return true
  if (portal.submission.status !== 'returned') return false
  return portal.submission.flags.some((f) => f.target.materialId === materialId)
}

/** In a returned submission, a share opens again only if the reviewer flagged it. */
export function compositionLineEditable(portal: Portal, materialId: string, ingredientId: string): boolean {
  if (portal.submission.status === 'draft') return true
  if (portal.submission.status !== 'returned') return false
  return portal.submission.flags.some((f) =>
    f.target.kind === 'composition' && f.target.materialId === materialId && f.target.ingredientId === ingredientId)
}

/** Likewise an ingredient's origins: open again only where an origin of it was flagged. */
export function ingredientOriginsEditable(portal: Portal, materialId: string, ingredientId: string): boolean {
  if (portal.submission.status === 'draft') return true
  if (portal.submission.status !== 'returned') return false
  return portal.submission.flags.some((f) =>
    f.target.kind === 'origin' && f.target.materialId === materialId && f.target.ingredientId === ingredientId)
}

/** Whole days from `now` until `iso`, never below zero. */
export function daysUntil(iso: string, now = new Date()): number {
  const ms = new Date(iso).getTime() - now.getTime()
  return Math.max(0, Math.ceil(ms / 86_400_000))
}
